/**
 * GA4 via gtag.js for STAVBOŠ + optional Google Ads conversion (AW).
 *
 * Measurement ID (priority):
 *   1. window.__STAVBOS_GA4_ID__  (runtime override / nginx inject)
 *   2. process.env.REACT_APP_GA4_ID
 *   3. process.env.VITE_GA4_MEASUREMENT_ID  (compat alias)
 *
 * Ads conversion (AC/DC MONT AW-…):
 *   1. window.__STAVBOS_AW_ID__ / __STAVBOS_AW_SEND_TO__
 *   2. process.env.REACT_APP_AW_ID / REACT_APP_AW_SEND_TO
 *
 * CRA inlines only static `process.env.REACT_APP_*` member access — keep those
 * static (no dynamic process.env[key]).
 *
 * Preferred long-term: link GA4 STAVBOŠ ↔ Ads and import `generate_lead`.
 * AW event fires at the same moment as generate_lead when SEND_TO is set.
 */

const PLACEHOLDER_IDS = new Set(["", "G-XXXXXXXX", "G-PLACEHOLDER", "undefined", "null", "AW-XXXXXXXX"]);

function pickId(...candidates) {
  for (const raw of candidates) {
    const id = String(raw || "").trim();
    if (id && !PLACEHOLDER_IDS.has(id)) return id;
  }
  return "";
}

export function getGa4Id() {
  if (typeof window !== "undefined") {
    const fromWindow = pickId(window.__STAVBOS_GA4_ID__, window.__GA4_MEASUREMENT_ID__);
    if (fromWindow) return fromWindow;
  }
  // CRA DefinePlugin inlines these member expressions — do not guard with typeof process.
  return pickId(process.env.REACT_APP_GA4_ID, process.env.VITE_GA4_MEASUREMENT_ID);
}

/** Google Ads conversion ID, e.g. AW-17980677497 */
export function getAwId() {
  if (typeof window !== "undefined") {
    const fromWindow = pickId(window.__STAVBOS_AW_ID__, window.__AW_ID__);
    if (fromWindow) return fromWindow;
  }
  return pickId(process.env.REACT_APP_AW_ID, process.env.VITE_AW_ID);
}

/**
 * Full send_to for conversion event, e.g. AW-17980677497/xxxxxxxx
 * If only AW ID is set, config loads the tag but no conversion event fires.
 */
export function getAwSendTo() {
  if (typeof window !== "undefined") {
    const fromWindow = pickId(window.__STAVBOS_AW_SEND_TO__, window.__AW_SEND_TO__);
    if (fromWindow) return fromWindow;
  }
  return pickId(process.env.REACT_APP_AW_SEND_TO, process.env.VITE_AW_SEND_TO);
}

function ensureGtag() {
  if (typeof window === "undefined") return null;
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== "function") {
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
  }
  return window.gtag;
}

function loadGtagScript(id) {
  if (!id || document.querySelector(`script[data-stavbos-gtag="${id}"]`)) return;
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
  s.dataset.stavbosGtag = id;
  document.head.appendChild(s);
}

let initialized = false;

/** Load gtag.js once and config GA4 (+ optional AW). SPA handles page_view. */
export function initGa4() {
  if (typeof window === "undefined" || initialized) return false;
  const ga4 = getGa4Id();
  const aw = getAwId();
  if (!ga4 && !aw) return false;

  const gtag = ensureGtag();
  if (!gtag) return false;

  // Prefer loading script with GA4 id; AW can share the same gtag.js loader.
  loadGtagScript(ga4 || aw);

  gtag("js", new Date());
  if (ga4) {
    gtag("config", ga4, {
      send_page_view: false,
      anonymize_ip: true,
    });
  }
  if (aw) {
    gtag("config", aw);
  }
  initialized = true;
  return true;
}

export function trackPageView(path, title) {
  const gtag = ensureGtag();
  if (!gtag || !getGa4Id()) return;
  const pagePath = path || (typeof window !== "undefined" ? window.location.pathname + window.location.search : "/");
  gtag("event", "page_view", {
    page_path: pagePath,
    page_title: title || (typeof document !== "undefined" ? document.title : undefined),
    page_location: typeof window !== "undefined" ? window.location.href : undefined,
  });
}

export function trackEvent(name, params = {}) {
  const gtag = ensureGtag();
  if (!gtag || !getGa4Id()) return;
  gtag("event", name, params);
}

/** Secondary: CTA / navigation toward creating a listing. */
export function trackBeginListing({ source } = {}) {
  trackEvent("begin_listing", {
    content_type: "cta",
    item_id: "novy-inzerat",
    ...(source ? { source } : {}),
  });
  trackEvent("select_content", {
    content_type: "cta",
    item_id: "novy-inzerat",
    ...(source ? { source } : {}),
  });
}

/**
 * Primary conversion: listing submitted successfully.
 * Fires GA4 generate_lead + submit_listing, and optional Ads AW conversion.
 */
export function trackSubmitListing({ listingId, category } = {}) {
  const payload = {
    ...(listingId ? { listing_id: listingId } : {}),
    ...(category ? { category } : {}),
  };
  trackEvent("generate_lead", payload);
  trackEvent("submit_listing", payload);

  const gtag = ensureGtag();
  const sendTo = getAwSendTo();
  if (gtag && sendTo) {
    const aw = getAwId();
    if (aw) gtag("config", aw);
    gtag("event", "conversion", {
      send_to: sendTo,
      ...payload,
    });
  }
}
